import { ScaleLinear, drag, select } from "d3";
import { clamp } from "lodash";
import { RefObject, useEffect, useState } from "react";

import { usePlayContext } from "../../../pages/Play/play-context";
import { Action, DragGroupElement } from "../../../types";
import { BAR_HANDLE_WIDTH } from "../components";
import { useBarConstraints } from "./use-bar-constraints";

export type UseMoveActionHandleProps = {
  action: Action;
  xStart: number;
  xEnd: number;
  scale: ScaleLinear<number, number>;
  startHandleRef: RefObject<SVGRectElement>;
  endHandleRef: RefObject<SVGRectElement>;
  canEdit: boolean;
};

export const useMoveActionHandle = ({
  action,
  xStart,
  xEnd,
  scale,
  startHandleRef,
  endHandleRef,
  canEdit,
}: UseMoveActionHandleProps) => {
  const { id: actionId, startTime, endTime } = action;
  const { frameStencilsApi, playStateApi } = usePlayContext();
  const { updateMovementTime } = frameStencilsApi;
  const { activeFrameStencils } = playStateApi;

  const { prevMaxTime, nextMinTime } = useBarConstraints({ action, frameStencils: activeFrameStencils, scale });

  // position of action bar only controlled by external props, used to calculate x offset
  const [staticXStart, setStaticXStart] = useState<number>(xStart);
  const [staticXEnd, setStaticXEnd] = useState<number>(xStart);
  // position of action bar that is controlled both by external props and internal drag events
  const [tempXStart, setTempXStart] = useState<number>(xEnd);
  const [tempXEnd, setTempXEnd] = useState<number>(xEnd);
  const [tempWidth, setTempWidth] = useState<number>(xEnd);

  useEffect(() => {
    setStaticXStart(xStart);
    setTempXStart(xStart);
    setStaticXEnd(xEnd);
    setTempXEnd(xEnd);
    setTempWidth(xEnd);
  }, [xStart, xEnd]);

  /** ======== Control Start Handle Drag ======== */
  useEffect(() => {
    const handle = startHandleRef.current;

    const domain = scale.domain();
    const range = scale.range();

    // min and max limits for handle drag
    const minX = Math.max(range[0] || 0, scale(prevMaxTime)) - scale(startTime);
    const maxX = staticXEnd - 4 * BAR_HANDLE_WIDTH;

    if (handle && canEdit) {
      select<SVGRectElement, undefined>(handle).call(
        drag<SVGRectElement, undefined>()
          .on("drag", function (event: DragGroupElement<SVGRectElement>) {
            // get new x position
            const { x, subject } = event;
            const { x: subjectX } = subject;
            const newX = x - (subjectX - staticXStart);
            const newXClamped = clamp(newX, minX, maxX);

            // update temp position while dragging
            setTempXStart(newXClamped);
            setTempWidth(staticXEnd - newXClamped);
          })
          .on("end", function (event: DragGroupElement<SVGRectElement>) {
            // get delta time position
            const { x, subject } = event;
            const { x: subjectX } = subject;
            const deltaX = x - (subjectX - staticXStart);
            const deltaTime = scale.invert(deltaX);
            const startTimeClamped = clamp(
              startTime + deltaTime,
              Math.max(domain[0] || 0, prevMaxTime),
              endTime - scale.invert(4 * BAR_HANDLE_WIDTH)
            );

            // update time in data state
            updateMovementTime({ actionId, startTime: startTimeClamped, endTime });
          })
      );
    }

    return () => {
      if (handle) select<SVGRectElement, undefined>(handle).on("mousedown.drag", null);
    };
  }, [
    startHandleRef,
    canEdit,
    actionId,
    startTime,
    endTime,
    scale,
    staticXStart,
    staticXEnd,
    updateMovementTime,
    prevMaxTime,
  ]);

  /** ======== Control End Handle Drag ======== */
  useEffect(() => {
    const handle = endHandleRef.current;

    const domain = scale.domain();
    const range = scale.range();

    // min and max limits for handle drag
    const minX = staticXStart + 4 * BAR_HANDLE_WIDTH;
    const maxX = Math.min(range[1] || 0, scale(nextMinTime)) - scale(startTime);

    if (handle) {
      select<SVGRectElement, undefined>(handle).call(
        drag<SVGRectElement, undefined>()
          .on("drag", function (event: DragGroupElement<SVGRectElement>) {
            // get new x position
            const { x, subject } = event;
            const { x: subjectX } = subject;
            const newX = x - (subjectX - staticXEnd);
            const newXClamped = clamp(newX, minX, maxX);

            setTempXEnd(newXClamped);
            setTempWidth(newXClamped - staticXStart);
          })
          .on("end", function (event: DragGroupElement<SVGRectElement>) {
            // get delta time position
            const { x, subject } = event;
            const { x: subjectX } = subject;
            const deltaX = x - subjectX;
            const deltaTime = scale.invert(deltaX);
            const endTimeClamped = clamp(
              endTime + deltaTime,
              startTime + scale.invert(4 * BAR_HANDLE_WIDTH),
              Math.min(domain[1] || 0, nextMinTime)
            );

            // update time in data state
            updateMovementTime({ actionId, startTime, endTime: endTimeClamped });
          })
      );
    }

    return () => {
      if (handle) select<SVGRectElement, undefined>(handle).on("mousedown.drag", null);
    };
  }, [endHandleRef, actionId, startTime, endTime, scale, staticXStart, staticXEnd, updateMovementTime, nextMinTime]);

  return { tempXStart, tempXEnd, tempWidth };
};
